import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/app/Navbar";
import Footer from "@/components/app/Footer";
import { api, imageUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

const ADDON_KEYS = ["cook", "cleaning", "wifi_boost", "airport_pickup"];

const emptyAddon = (key) => ({ key, enabled: false, daily: "", weekly: "", monthly: "", per_service: "" });

const ALLOWED_BILLING = {
  cook: ["daily", "weekly", "monthly"],
  cleaning: ["weekly", "monthly", "per_service"],
  wifi_boost: ["monthly"],
  airport_pickup: ["per_service"],
};

export default function ListingForm() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    title: "", city: "Kharghar", area: "", description: "",
    min_stay_nights: 7,
    price_per_night: 2499, price_weekly: 15999, price_15_nights: 34999, price_monthly: 64999,
    amenities: "",
    images: [],
  });
  const [addons, setAddons] = useState(ADDON_KEYS.map(emptyAddon));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/listings/${id}`).then(({ data }) => {
      setForm({
        title: data.title, city: data.city, area: data.area, description: data.description,
        min_stay_nights: data.min_stay_nights,
        price_per_night: data.price_per_night, price_weekly: data.price_weekly,
        price_15_nights: data.price_15_nights, price_monthly: data.price_monthly,
        amenities: (data.amenities || []).join(", "),
        images: data.images || [],
      });
      const map = Object.fromEntries((data.addons || []).map((a) => [a.key, a]));
      setAddons(ADDON_KEYS.map((k) => map[k] ? {
        key: k,
        enabled: map[k].enabled !== false,
        daily: map[k].daily || "",
        weekly: map[k].weekly || "",
        monthly: map[k].monthly || "",
        per_service: map[k].per_service || "",
      } : emptyAddon(k)));
    });
  }, [id, isEdit]);

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      const paths = [];
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      setForm((p) => ({ ...p, images: [...p.images, ...paths] }));
      toast.success(`${paths.length} image(s) uploaded`);
    } catch (e) {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const removeImg = (path) => setForm((p) => ({ ...p, images: p.images.filter((x) => x !== path) }));

  const setAddon = (key, patch) =>
    setAddons((arr) => arr.map((a) => (a.key === key ? { ...a, ...patch } : a)));

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        min_stay_nights: Number(form.min_stay_nights),
        price_per_night: Number(form.price_per_night),
        price_weekly: Number(form.price_weekly),
        price_15_nights: Number(form.price_15_nights),
        price_monthly: Number(form.price_monthly),
        amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
        addons: addons.map((a) => ({
          key: a.key,
          enabled: a.enabled,
          daily: a.daily === "" ? null : Number(a.daily),
          weekly: a.weekly === "" ? null : Number(a.weekly),
          monthly: a.monthly === "" ? null : Number(a.monthly),
          per_service: a.per_service === "" ? null : Number(a.per_service),
        })),
      };
      if (isEdit) {
        await api.put(`/listings/${id}`, payload);
        toast.success("Listing updated");
      } else {
        await api.post("/listings", payload);
        toast.success("Listing published");
      }
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (authLoading) return <div className="min-h-screen"><Navbar /></div>;
  if (!user) { nav("/dashboard"); return null; }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 lg:px-10 pt-10 pb-24">
        <div className="overline">{isEdit ? "Edit listing" : "New listing"}</div>
        <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl tracking-tight mb-10">
          {isEdit ? "Refine your stay" : "Curate a new long-stay home"}
        </h1>

        <form onSubmit={onSave} className="space-y-10">
          {/* Basics */}
          <section className="surface-card p-8">
            <h2 className="font-serif-display text-2xl mb-6">Basics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="overline">Title</label>
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="input-earthy mt-2" placeholder="e.g. Urban Nest Studio" data-testid="form-title" />
              </div>
              <div>
                <label className="overline">City</label>
                <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="input-earthy mt-2" data-testid="form-city" />
              </div>
              <div>
                <label className="overline">Neighborhood / Area</label>
                <input required value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })}
                  className="input-earthy mt-2" placeholder="e.g. Sector 12" data-testid="form-area" />
              </div>
              <div className="md:col-span-2">
                <label className="overline">Description</label>
                <textarea required rows={4} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="input-earthy mt-2 resize-none" data-testid="form-description" />
              </div>
              <div>
                <label className="overline">Min stay (nights)</label>
                <input type="number" min={1} required value={form.min_stay_nights}
                  onChange={(e) => setForm({ ...form, min_stay_nights: e.target.value })}
                  className="input-earthy mt-2" data-testid="form-min-stay" />
              </div>
              <div>
                <label className="overline">Amenities (comma-separated)</label>
                <input value={form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })}
                  className="input-earthy mt-2" placeholder="WiFi, AC, Workspace" data-testid="form-amenities" />
              </div>
            </div>
          </section>

          {/* Pricing */}
          <section className="surface-card p-8">
            <h2 className="font-serif-display text-2xl mb-6">Pricing tiers</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                ["price_per_night", "Per night"],
                ["price_weekly", "Weekly"],
                ["price_15_nights", "15 nights"],
                ["price_monthly", "Monthly"],
              ].map(([k, l]) => (
                <div key={k}>
                  <label className="overline">{l}</label>
                  <input type="number" min={0} required value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="input-earthy mt-2" data-testid={`form-${k}`} />
                </div>
              ))}
            </div>
          </section>

          {/* Images */}
          <section className="surface-card p-8">
            <h2 className="font-serif-display text-2xl mb-2">Images</h2>
            <p className="text-xs mb-5" style={{ color: "var(--text-2)" }}>Upload at least one cover photo (jpg, png, webp).</p>
            <label
              htmlFor="file-input"
              className="flex flex-col items-center justify-center rounded-xl p-8 cursor-pointer transition-colors"
              style={{ border: "2px dashed var(--line)", background: "var(--bone)" }}
              data-testid="upload-dropzone"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-6 h-6 text-[color:var(--forest)]" />}
              <span className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>
                {uploading ? "Uploading…" : "Click to upload or drop files here"}
              </span>
              <input id="file-input" type="file" accept="image/*" multiple className="hidden"
                onChange={(e) => uploadFiles(e.target.files)} data-testid="upload-input" />
            </label>
            {form.images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-5">
                {form.images.map((p, i) => (
                  <div key={p} className="relative rounded-xl overflow-hidden" data-testid={`uploaded-img-${i}`}>
                    <img src={imageUrl(p)} alt="" className="w-full h-32 object-cover" />
                    <button type="button" onClick={() => removeImg(p)}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90"
                      data-testid={`remove-img-${i}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Addons */}
          <section className="surface-card p-8">
            <h2 className="font-serif-display text-2xl mb-2">Add-ons offered</h2>
            <p className="text-xs mb-5" style={{ color: "var(--text-2)" }}>
              Enable any extras you offer. Leave a field blank to use the marketplace default price.
            </p>
            <div className="space-y-4">
              {addons.map((a) => (
                <div key={a.key} className="rounded-xl p-5" style={{
                  background: a.enabled ? "rgba(34,60,51,0.04)" : "var(--bone)",
                  border: a.enabled ? "2px solid var(--forest)" : "1px solid var(--line)",
                }} data-testid={`addon-form-${a.key}`}>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={a.enabled}
                      onChange={(e) => setAddon(a.key, { enabled: e.target.checked })}
                      data-testid={`addon-toggle-${a.key}`} />
                    <span className="font-medium capitalize">{a.key.replace("_", " ")}</span>
                  </label>
                  {a.enabled && (
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                      {ALLOWED_BILLING[a.key].map((b) => (
                        <div key={b}>
                          <label className="overline">{b.replace("_", " ")} (₹)</label>
                          <input type="number" min={0} value={a[b] ?? ""} placeholder="default"
                            onChange={(e) => setAddon(a.key, { [b]: e.target.value })}
                            className="input-earthy mt-2"
                            data-testid={`addon-${a.key}-${b}`} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <div className="flex gap-3 justify-end">
            <button type="button" onClick={() => nav("/dashboard")} className="btn-pill btn-outline-dark" data-testid="cancel-btn">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-pill btn-primary" data-testid="save-listing-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isEdit ? "Save changes" : "Publish listing"}
            </button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}
